import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import type { UserRole } from '@/types/contracts';

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    title: string;
    roles?: UserRole[];
    creatorTab?: string;
    /** 标记营销盒管理台新增页面；未标记的业务页必须来自冻结 CreatorHub。 */
    management?: boolean;
  }
}

const managementRoutes: RouteRecordRaw[] = [
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { title: '工作台', management: true },
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/views/DevicesView.vue'),
    meta: { title: '营销盒', management: true },
  },
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('@/views/TasksView.vue'),
    meta: { title: '任务', management: true },
  },
  {
    path: '/platform-accounts',
    name: 'platform-accounts',
    component: () => import('@/views/PlatformAccountsView.vue'),
    meta: { title: '平台账号', management: true },
  },
  {
    path: '/platform-accounts-login',
    name: 'platform-accounts-login',
    redirect: '/platform-accounts',
    meta: { title: '平台账号登录', management: true },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/AdminUsersView.vue'),
    meta: { title: '用户管理', roles: ['SUPER_ADMIN'], management: true },
  },
  {
    path: '/admin/devices',
    name: 'admin-devices',
    component: () => import('@/views/AdminDevicesView.vue'),
    meta: { title: '全部设备', roles: ['SUPER_ADMIN'], management: true },
  },
  {
    path: '/admin/marketing-box',
    name: 'admin-marketing-box',
    component: () => import('@/views/AdminMarketingBoxView.vue'),
    meta: { title: '营销盒资格', roles: ['SUPER_ADMIN'], management: true },
  },
];

// 所有普通业务路由共享同一个冻结原版组件，避免某个页签意外回退到管理台视图。
const creatorHubComponent = () => import('@/views/CreatorHubView.vue');

/**
 * CreatorHub 原版路由定义。
 *
 * <p>这些页面只改变 URL 到原版页签的映射，不复制或重写原版内容；
 * {@link CreatorHubView} 通过冻结静态资产提供原版视觉和交互。</p>
 */
export const creatorHubRoutes: RouteRecordRaw[] = [
  {
    path: '/creatorhub',
    name: 'creatorhub',
    component: creatorHubComponent,
    meta: { title: 'CreatorHub 工作台', creatorTab: 'overview', management: false },
  },
  ...[
    ['content', 'hub', '内容'],
    ['collection', 'collections', '内容采集'],
    ['collection-keywords', 'collections', '关键词采集'],
    ['collection-share-links', 'collections', '分享链接'],
    ['monitoring-content', 'monitors', '内容监控'],
    ['monitoring-comments', 'comments', '评论监控'],
    ['monitoring-danmaku', 'danmaku', '弹幕监控'],
    ['publishing', 'publish', '内容发布'],
    ['engagement-comments', 'autocomment', '评论任务'],
    ['messages', 'accounts', '私信'],
    ['reports', 'overview', '报表'],
    ['settings-system', 'settings', '系统设置'],
    ['risk-control', 'risk-control', '风控'],
    ['notifications', 'notifications', '通知'],
    ['tasks-review-queue', 'queue', '任务队列'],
  ].map(([path, creatorTab, title]) => ({
    path: `/${path}`,
    name: `creatorhub-${path}`,
    component: creatorHubComponent,
    meta: { title, creatorTab, management: false },
  } as RouteRecordRaw)),
];

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    meta: { title: '花生智能营销盒' },
    children: [
      { path: '', redirect: '/dashboard' },
      ...managementRoutes.map((route) => ({
        ...route,
        path: route.path.replace(/^\//, ''),
      })),
    ],
  },
  ...creatorHubRoutes,
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue'), meta: { public: true, title: '页面不存在' } },
];

export function canAccessRoute(role: UserRole | undefined, roles?: UserRole[]): boolean {
  return !roles?.length || Boolean(role && roles.includes(role));
}

const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach(async (to) => {
  document.title = `${to.meta.title} | 花生智能营销盒`;
  const auth = useAuthStore();
  await auth.initialize();
  if (to.meta.public) {
    if (to.name === 'login' && auth.isAuthenticated) return { name: 'dashboard' };
    return true;
  }
  if (!auth.isAuthenticated) return { name: 'login', query: { redirect: to.fullPath } };
  if (!canAccessRoute(auth.user?.role, to.meta.roles)) return { name: 'dashboard' };
  return true;
});

export default router;
